import { HeadingFive } from "components/reusable/typography/Typography.style";
import { useFormik } from "formik";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";

import * as Yup from "yup";
import {
  FormHelperText,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { Delete } from "@mui/icons-material";
import { Button } from "components/reusable/button/Button";
import { SyncLoader } from "react-spinners";
import AdditionalNotes from "../../modals/AdditionalNotes";
import ValidateTaxModal from "components/modals/ValidateTaxModal";
import { apiReq } from "services/Requests";
import { API_ROUTES } from "utils/apiRoutes.constant";
import { useNavigate } from "react-router-dom";
import { PageLoaderContext } from "context/PageLoaderProvider";

const infoToast = (message = "") => toast.info(message);
const errorToast = (message = "") => toast.error(message);

const validationSchema = Yup.object()
  .shape({
    income: Yup.string(),
    incomeDoc: Yup.mixed().nullable().notRequired(),
  })
  .test(
    "oneOfRequired",
    "Income is required (enter an amount or upload a document)",
    function (values) {
      const { income, incomeDoc } = values || {};
      const hasIncome = income && income.trim() !== "";
      const hasDoc = !!incomeDoc; // true if a file is present

      if (hasIncome || hasDoc) return true;

      // Attach error to the `income` field so Formik displays it
      return this.createError({
        path: "income",
        message: "Income is required (enter an amount or upload a document)",
      });
    }
  );

const Income = ({ rowData }) => {
  const fileInputRef = useRef();
  const [isAdditionalModal, setIsAdditionalModal] = useState(false);
  const [isValidateTaxModal, setIsValidateTaxModal] = useState(false);
  const [additionalNotes, setadditionalnotes] = useState();
  const [, setLoading] = useState(false);
  const [incomeData, setIncomeData] = useState();
  const generalBusinessMainId = localStorage.getItem("generalBusinessMainId");
  const editForm = localStorage.getItem("editForm");
  const clientId = localStorage.getItem("clientId");
  const generalId = JSON.parse(localStorage.getItem("generalBusinessID"));
  const [generalBusinessId] = useState(
    editForm === "true" ? generalId : generalBusinessMainId
  );
  const navigate = useNavigate();
  const { setOpen } = useContext(PageLoaderContext);

  const formik = useFormik({
    initialValues: {
      income: incomeData?.income || "",
      incomeDoc: incomeData?.incomeDoc || null,
      incomeDocFileName: incomeData?.incomeDocFileName || "",
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      console.log(values, "___vals");
      try {
        const formData = new FormData();
        if (values.income && values.income.trim() !== "") {
          formData.append("income", values.income);
        }
        if (values.incomeDoc) {
          formData.append("incomeDoc", values.incomeDoc);
          formData.append("incomeDocFileName", values.incomeDocFileName);
        }
        formData.append("clientId", clientId);
        if (editForm === "true" && incomeData?._id) {
          formData.append("incomeId", incomeData?._id);
        } else {
          formData.append(
            "generalBusinessMainId",
            generalBusinessMainId ? generalBusinessMainId : rowData?._id
          );
        }
        const { data: { data = {} } = {} } = await apiReq(
          !incomeData?._id ? API_ROUTES.addIncome : API_ROUTES.updateIncome,
          "POST",
          formData,
          undefined,
          false
        );
        console.log(data, "___res");
        localStorage.setItem("incomeId", data?._id);
        infoToast(
          data?._id
            ? "Income updated successfully"
            : "Income saved successfully"
        );
        setTimeout(() => {
          setIsAdditionalModal(true);
        }, 1500);
      } catch (error) {
        errorToast(
          error?.message ||
            "An unexpected error occurred. Please try again later."
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  console.log(formik.errors, "__errs");

  const handleFileChange = (event) => {
    const file = event.currentTarget.files[0];
    if (file) {
      formik.setFieldValue("incomeDoc", file);
      formik.setFieldValue("income", "");
      formik.setFieldValue("incomeDocFileName", file.name);
    }
  };

  const handleIncomeChange = (event) => {
    formik.setFieldValue("income", event.target.value);
    if (formik.values.incomeDoc) {
      formik.setFieldValue("incomeDoc", null);
      formik.setFieldValue("incomeDocFileName", "");
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleFormSubmit = async () => {
    try {
      // Prepare data to be uploaded
      setLoading(true);
      const uploadData = { additionalNotes, generalBusinessId };

      await apiReq(API_ROUTES.updateGeneralBusiness, "POST", uploadData);
      infoToast("Info saved successfully");
      localStorage.setItem("editForm", false);
      setTimeout(() => {
        navigate("/review");
      }, 1500);

      // localStorage.removeItem('generalBusinessMainId');
    } catch (error) {
      errorToast(
        error?.message ||
          "An unexpected error occurred. Please try again later."
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchdata = useCallback(async () => {
    try {
      setOpen(true);
      const { data: { data = {} } = {} } = await apiReq(
        API_ROUTES.getGeneralBusinessByID +
          (rowData?._id || generalBusinessMainId),
        "POST",
        { clientId: clientId }
      );
      console.log(data, "___res");
      if (data) {
        setIncomeData(data?.incomeId);
        setadditionalnotes(data?.additionalNotes);
      }
    } catch (error) {
      errorToast(
        error?.message ||
          "An unexpected error occurred. Please try again later."
      );
    } finally {
      setOpen(false);
    }
  }, [rowData?._id, clientId, generalBusinessMainId, setOpen]);

  useEffect(() => {
    if (rowData?._id || generalBusinessMainId) fetchdata();
  }, [rowData?._id, generalBusinessMainId, fetchdata]);

  console.log(formik?.values, "__errs");

  return (
    <>
      <form onSubmit={formik.handleSubmit}>
        <div>
          <HeadingFive color="#000" fontSize="18px" fontWeight="500">
            Income
          </HeadingFive>
          <Stack
            direction="column"
            sx={{ width: "40%", marginTop: "30px" }}
            spacing={2}
          >
            <TextField
              label="Income *"
              id="income"
              name="income"
              value={
                formik.values.incomeDoc
                  ? formik.values.incomeDoc.name
                  : formik.values.income
              }
              onChange={handleIncomeChange}
              onBlur={formik.handleBlur}
              error={formik.touched.income && Boolean(formik.errors.income)}
              helperText={formik.touched.income && formik.errors.income}
              size="small"
              type="text"
              disabled={!!formik.values.incomeDoc}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      component="span"
                      onClick={() =>
                        fileInputRef.current && fileInputRef.current.click()
                      }
                    >
                      <CloudUploadIcon />
                    </IconButton>
                    <input
                      type="file"
                      ref={fileInputRef}
                      style={{ display: "none" }}
                      onChange={handleFileChange}
                    />
                  </InputAdornment>
                ),
              }}
            />

            {formik.values.incomeDoc && (
              <FormHelperText>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <p>{formik.values.incomeDocFileName}</p>
                  <span
                    style={{
                      cursor: "pointer",
                      color: "#0b2558",
                      display: "flex",
                    }}
                    onClick={() => {
                      formik.setFieldValue("incomeDoc", null);
                      formik.setFieldValue("incomeDocFileName", "");
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }}
                  >
                    <Delete />
                  </span>
                </Stack>
              </FormHelperText>
            )}
          </Stack>
        </div>
        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <Button
            text={
              formik.isSubmitting ? (
                <SyncLoader color={"#FFFFFF"} size={8} />
              ) : (
                "Submit"
              )
            }
            width="fit-content"
            fontSize="20px"
            padding="10px 40px"
            backgroundColor="#0B2558"
            onClick={formik.handleSubmit}
            alignSelf="flex-end"
          />
        </Stack>
      </form>
      <AdditionalNotes
        isModalOpen={isAdditionalModal}
        closeModal={() => setIsAdditionalModal(false)}
        openSecondModal={() => setIsValidateTaxModal(true)}
        additionalNotes={additionalNotes}
        setadditionalnotes={setadditionalnotes}
      />
      <ValidateTaxModal
        isModalOpen={isValidateTaxModal}
        closeModal={() => setIsValidateTaxModal(false)}
        paragraph="Are you sure you want to validate this Business Info Form?"
        handleYesClick={handleFormSubmit}
      />
    </>
  );
};

export default Income;
